import React from "react";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ArrowUpward, ArrowDownward, DragIndicator } from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TapToRank } from "../ranking/TapToRank";

type Props = {
  items: string[];
  value: string[];
  onChange: (ordered: string[]) => void;
};

export const SkillRanker: React.FC<Props> = ({ items, value, onChange }) => {
  const order = value.length ? value : items;
  const isSmall = useMediaQuery("(max-width:600px)");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length || from === to) return;
    onChange(arrayMove(order, from, to));
  };

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onChange(arrayMove(order, from, to));
  };

  if (isSmall) {
    return (
      <TapToRank 
        items={items} 
        value={value} 
        onChange={onChange} 
      />
    );
  }

  return (
    <Box maxWidth={720} mx="auto">
      <Typography variant="body2" color="text.secondary" gutterBottom textAlign="center">
        (1 = Highest, 10 = Lowest)
      </Typography>

      <Paper variant="outlined" sx={{ p: 0.5, border: "none" }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            <List disablePadding>
              {order.map((label) => (
                <SortableRow key={label} id={label} label={label} />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </Paper>
    </Box>
  );
};

function SortableRow({ id, label }: { id: string; label: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <ListItem
      ref={setNodeRef}
      disableGutters
      {...attributes}
      {...listeners}
      sx={{
        px: 1.5,
        py: 1,
        "& + li": { mt: 0.5 },
        opacity: isDragging ? 0.6 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        bgcolor: "background.paper",
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        touchAction: "none",
      }}
    >
      <ListItemIcon sx={{ minWidth: 28, color: "text.disabled" }}>
        <DragIndicator fontSize="small" sx={{ width: 16, height: 16 }} />
      </ListItemIcon>

      <ListItemText primary={label} />
    </ListItem>
  );
}
